import React, {useRef} from 'react';
import {ImageBackground, StyleSheet, View} from 'react-native';
import {
  ActivityIndicator,
  Banner,
  Chip,
  Divider,
  ProgressBar,
  Text,
  useTheme,
} from 'react-native-paper';
import {useQuery, useQueryClient} from 'react-query';
import {EncoderProject} from '../../types';
import {
  getFileOpfs,
  encodeMedia,
  getVideoWT,
  getById,
} from '../../api';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {useIsFocused} from '@react-navigation/native';
import {niceBytes} from '../../api/util';
import {setEncoderProgress} from '../../store/slices/encoder';

const styles = StyleSheet.create({
  video: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  chip: {
    fontSize: 18,
  },
});
export default function EncoderBanner({
  projectId,
  height,
  folderId,
}: {
  projectId: string;
  height: number;
  folderId: string;
}) {
  const dispatch = useAppDispatch();
  const state = useAppSelector(state => state.encoder);
  const theme = useTheme();
  const queryClient = useQueryClient();
  const mediaData = useQuery(
    ['projectdata', projectId],
    async () => await getById(projectId, folderId),
    {
      enabled: projectId ? true : false,
    },
  );
  const focused = useIsFocused();

  const mediaRes = mediaData.data as EncoderProject | undefined;
  // const {encoder} = mediaRes

  const localMediaQuery = useQuery(
    ['localMedia', projectId],
    async () => {
      if (!mediaRes?.id) return undefined;
      // const folder = await getById(mediaRes.folderId)
      let file;
      try {
        file = await getFileOpfs(mediaRes);
      } catch (err) {
        console.error(err);
      }

      if (mediaRes.extractor === 'wt' && !file)
        file = await getVideoWT(mediaRes);

      return file;
    },
    {
      enabled:
        mediaData.isFetched && focused && mediaRes?.id !== undefined
          ? true
          : false,
    },
  );

  const encodedMediaQuery = useQuery(
    ['encodedMedia', projectId],
    async () =>
      mediaRes?.id &&
      encodeMedia(mediaRes, params =>
        dispatch(setEncoderProgress(params.progress)),
      ),
    {
      enabled:
        mediaData.isFetched && focused && mediaRes?.id !== undefined
          ? true
          : false,
    },
  );

  const encodedFileSize = useQuery(
    ['encodedFileSize', projectId],
    async () => {
      // console.info("encoded url", encodedMediaQuery.data)
      if (!encodedMediaQuery.data) return '0';
      const head = await fetch(encodedMediaQuery.data, {
        cache: 'no-cache',
      });
      // console.info('head', head)
      return head.headers.get('Content-Length')?.toString();
    },
    {
      enabled:
        !mediaData.isFetching &&
        focused &&
        encodedMediaQuery.data &&
        encodedMediaQuery.data.search('blob') > -1
          ? true
          : false,

      retry: false,
    },
  );

  const mediaLoading =
    mediaData.isFetching || localMediaQuery.isFetching;
  console.info('encodedFileSize', encodedFileSize.data);
  const mediaSrc = localMediaQuery.data;
  const video2 = useRef<JSX.IntrinsicElements['video']>();
  const video1 = useRef<JSX.IntrinsicElements['video']>();
  if (!mediaRes) {
    return <ActivityIndicator size={72} />;
  }
  return (
    <>
      <Divider />
      <Banner
        visible={projectId ? true : false}
        theme={theme}
        style={{
          backgroundColor: 'black',
          padding: 0,
          margin: 0,
        }}
        contentStyle={{
          width: '100%',
          height: '100%',
          maxWidth: 'auto',
          padding: 0,
          margin: 0,
        }}>
        <View
          style={{
            width: '50%',
            height: height / 2,
            borderRightWidth: 1,
            borderRightColor: 'rgb(73, 69, 79)',
          }}>
          <Text style={{textAlign: 'left', zIndex: 10}}>
            <Chip textStyle={styles.chip}>
              Before: {mediaRes?.size && niceBytes(mediaRes.size)}
            </Chip>
          </Text>
          {mediaLoading ? <ActivityIndicator size={72} /> : null}

          {projectId && !mediaLoading && focused ? (
            <video
              controls={true}
              style={styles.video}
              onPlaying={e => {
                const {currentTime} = e.nativeEvent.target;
                window.video1 = e.nativeEvent.target;
                if (video2.current)
                  video2.current.currentTime = currentTime;
                if (video2.current && video2.current.paused)
                  video2.current.play();
              }}
              onPause={e => {
                const {currentTime} = e.nativeEvent.target;
                if (video2.current)
                  video2.current.currentTime = currentTime;
                if (video2.current && !video2.current.paused)
                  video2.current.pause();
              }}
              onEnded={p => {
                console.info('ended', p);
              }}
              onTimeUpdate={e => {
                const {currentTime, paused} = e.nativeEvent.target;
                if (video2.current && paused)
                  video2.current.currentTime = currentTime;
              }}
              ref={video1}
              src={mediaSrc}
            />
          ) : null}
        </View>
        <View
          style={{
            width: '50%',
            height: height / 2,
            borderLeftWidth: 1,
            borderLeftColor: 'rgb(73, 69, 79)',
          }}>
          {encodedMediaQuery.isFetching ? (
            <>
              <Text style={{textAlign: 'right', zIndex: 10}}> </Text>
              <ProgressBar
                style={{
                  height: '100%',
                  position: 'absolute',
                  top: -25,
                }}
                progress={state.encoderProgress}
              />
              <View
                style={{
                  position: 'absolute',
                  top: '40%',
                  display: 'flex',
                  width: '100%',
                }}>
                <Text style={{textAlign: 'center', color: 'coral'}}>
                  Loading{' '}
                  {parseFloat(
                    Math.abs(state.encoderProgress * 100),
                  ).toFixed(2)}
                  %
                </Text>
              </View>
            </>
          ) : (
            <Text style={{textAlign: 'right', zIndex: 10}}>
              <Chip textStyle={styles.chip}>
                After:{' '}
                {!encodedFileSize.isFetching &&
                  encodedFileSize.data &&
                  niceBytes(encodedFileSize.data)}
              </Chip>
              <a href={encodedMediaQuery.data} download={true}>
                <Chip textStyle={styles.chip} onPress={() => {}}>
                  Download
                </Chip>
              </a>
            </Text>
          )}

          {projectId && !encodedMediaQuery.isFetching && focused ? (
            <>
              {mediaRes?.encoding?.output === 'mp4' && (
                <video
                  controls={true}
                  style={styles.video}
                  onPlaying={e => {
                    if (video1.current?.paused)
                      video1.current?.play();
                  }}
                  onPause={e => {
                    if (!video1.current?.paused)
                      video1.current?.pause();
                  }}
                  ref={video2}
                  src={encodedMediaQuery.data}
                />
              )}
              {mediaRes?.encoding?.output === 'gif' && (
                <ImageBackground
                  source={{uri: encodedMediaQuery.data}}
                  resizeMode="contain"
                  style={styles.video}
                />
              )}
            </>
          ) : null}
        </View>
      </Banner>
      <Divider />
    </>
  );
}
